import * as THREE from 'three';
import { fbm3d } from '../../visual';

export interface TerrainOptions {
  readonly size: number;
  readonly segments: number;
  readonly baseColor: number;
  readonly accentColor: number;
  readonly detailColor: number;
  readonly displacement: number;
  readonly largeFrequency?: number;
  readonly mediumFrequency?: number;
  readonly detailFrequency?: number;
}

const WHITE = new THREE.Color(0xffffff);
const DARK = new THREE.Color(0x1a1a2a);

export class Terrain {
  readonly object3D: THREE.Mesh;
  readonly size: number;
  readonly segments: number;
  readonly displacement: number;
  private readonly largeFreq: number;
  private readonly mediumFreq: number;
  private readonly detailFreq: number;
  private readonly seed: THREE.Vector3;
  private readonly heightData: Float32Array;
  private readonly gridSize: number;
  private readonly halfSize: number;
  private readonly cellSize: number;

  constructor(options: TerrainOptions) {
    this.size = options.size;
    this.segments = options.segments;
    this.displacement = options.displacement;
    this.largeFreq = options.largeFrequency ?? 0.009;
    this.mediumFreq = options.mediumFrequency ?? 0.034;
    this.detailFreq = options.detailFrequency ?? 0.115;
    this.halfSize = options.size / 2;
    this.gridSize = options.segments + 1;
    this.cellSize = options.size / options.segments;
    this.seed = new THREE.Vector3(
      Math.random() * 40,
      0,
      Math.random() * 40
    );

    const geo = new THREE.PlaneGeometry(
      options.size,
      options.size,
      options.segments,
      options.segments
    );
    geo.rotateX(-Math.PI / 2);

    const position = geo.attributes['position'] as THREE.BufferAttribute;
    this.heightData = new Float32Array(this.gridSize * this.gridSize);

    let minH = Infinity;
    let maxH = -Infinity;

    for (let i = 0; i < position.count; i++) {
      const x = position.getX(i);
      const z = position.getZ(i);
      const h = this.sampleRawHeight(x, z);
      position.setY(i, h);

      const gi = Math.round((x + this.halfSize) / this.cellSize);
      const gj = Math.round((z + this.halfSize) / this.cellSize);
      const gClampI = Math.max(0, Math.min(this.segments, gi));
      const gClampJ = Math.max(0, Math.min(this.segments, gj));
      this.heightData[gClampJ * this.gridSize + gClampI] = h;

      if (h < minH) minH = h;
      if (h > maxH) maxH = h;
    }
    position.needsUpdate = true;
    geo.computeVertexNormals();

    const colors = new Float32Array(position.count * 3);
    const normalAttr = geo.attributes['normal'] as THREE.BufferAttribute;

    const base = new THREE.Color(options.baseColor);
    const accent = new THREE.Color(options.accentColor);
    const detail = new THREE.Color(options.detailColor);
    const peak = base.clone().lerp(WHITE, 0.45);
    const shadow = base.clone().lerp(DARK, 0.25);
    const tmp = new THREE.Color();
    const range = Math.max(maxH - minH, 0.001);

    for (let i = 0; i < position.count; i++) {
      const h = position.getY(i);
      const x = position.getX(i);
      const z = position.getZ(i);
      const ny = normalAttr.getY(i);
      const altitude = (h - minH) / range;
      const steepness = Math.max(0, 1 - ny);

      tmp.copy(base);
      if (altitude > 0.28) {
        tmp.lerp(accent, Math.min((altitude - 0.28) * 1.1, 0.65));
      }
      if (altitude > 0.72) {
        tmp.lerp(peak, Math.min((altitude - 0.72) * 2.5, 0.7));
      }
      if (altitude < 0.22) {
        tmp.lerp(shadow, Math.min((0.22 - altitude) * 1.8, 0.35));
      }
      if (steepness > 0.32) {
        tmp.lerp(detail, Math.min((steepness - 0.32) * 1.5, 0.75));
      }

      const patches = fbm3d(
        x * 0.09 + this.seed.x * 1.7,
        0,
        z * 0.09 + this.seed.z * 1.7,
        3
      );
      if (patches > 0.22) {
        tmp.lerp(detail, Math.min((patches - 0.22) * 0.7, 0.25));
      }

      colors[i * 3] = tmp.r;
      colors[i * 3 + 1] = tmp.g;
      colors[i * 3 + 2] = tmp.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const material = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.94,
      metalness: 0,
      flatShading: false
    });

    this.object3D = new THREE.Mesh(geo, material);
    this.object3D.name = 'terrain';
    this.object3D.receiveShadow = false;
  }

  // Optional collision-only floor. When set, `getCollisionGroundY` clamps
  // its result to >= `collisionFloorY` inside `collisionFloorRadiusSq`. The
  // visible terrain mesh is unaffected — this exists so the FPS controller
  // can keep the player from walking under a water/lake plane that's
  // covering a deep valley. Center is the world origin (matches the
  // biome water surface, which is centred there).
  private collisionFloorY: number | null = null;
  private collisionFloorRadiusSq = 0;

  /** Set a circular "collision floor" centered at the origin. While
   *  active, `getCollisionGroundY` clamps the ground sample to at least
   *  `floorY` inside `radius` so the FPS player can't sink below a
   *  visible water plane covering a submerged valley. Pass null to
   *  disable. */
  setCollisionFloor(floorY: number | null, radius = 0): void {
    this.collisionFloorY = floorY;
    this.collisionFloorRadiusSq = radius * radius;
  }

  /** Variant of `getHeightAt` that respects the active collision floor.
   *  Used by the FPS / ship controllers; placement code keeps using
   *  `getHeightAt` so flora/ore continue to follow the visible mesh. */
  getCollisionGroundY(x: number, z: number): number {
    const raw = this.getHeightAt(x, z);
    if (this.collisionFloorY === null) return raw;
    if (this.collisionFloorRadiusSq <= 0) return raw;
    const distSq = x * x + z * z;
    if (distSq > this.collisionFloorRadiusSq) return raw;
    return Math.max(raw, this.collisionFloorY);
  }

  getHeightAt(x: number, z: number): number {
    const gu = (x + this.halfSize) / this.cellSize;
    const gv = (z + this.halfSize) / this.cellSize;

    if (
      gu < 0 ||
      gu > this.segments ||
      gv < 0 ||
      gv > this.segments
    ) {
      return this.sampleRawHeight(x, z);
    }

    const i0 = Math.floor(gu);
    const j0 = Math.floor(gv);
    const i1 = Math.min(i0 + 1, this.segments);
    const j1 = Math.min(j0 + 1, this.segments);
    const fu = gu - i0;
    const fv = gv - j0;

    const h00 = this.heightAtGrid(i0, j0);
    const h10 = this.heightAtGrid(i1, j0);
    const h01 = this.heightAtGrid(i0, j1);
    const h11 = this.heightAtGrid(i1, j1);

    const a = h00 * (1 - fu) + h10 * fu;
    const b = h01 * (1 - fu) + h11 * fu;
    return a * (1 - fv) + b * fv;
  }

  private heightAtGrid(i: number, j: number): number {
    return this.heightData[j * this.gridSize + i] ?? 0;
  }

  private sampleRawHeight(x: number, z: number): number {
    const sx = this.seed.x;
    const sz = this.seed.z;
    const large = fbm3d(x * this.largeFreq + sx, 0, z * this.largeFreq + sz, 4, 0.55);
    const medium = fbm3d(
      x * this.mediumFreq + sx * 1.4,
      0,
      z * this.mediumFreq + sz * 1.4,
      3,
      0.5
    );
    const detail = fbm3d(
      x * this.detailFreq + sx * 2.1,
      0,
      z * this.detailFreq + sz * 2.1,
      2,
      0.5
    );

    const softLarge = Math.sign(large) * Math.pow(Math.abs(large), 0.85);

    return (
      softLarge * this.displacement * 0.82 +
      medium * this.displacement * 0.18 +
      detail * this.displacement * 0.03
    );
  }
}
